import { Helmet } from "react-helmet";

/**
 * Reusable JSON-LD injectors for SEO + AEO (LLM citability).
 *
 * Each component renders <script type="application/ld+json"> via Helmet so the
 * payload ends up in <head>. Pass plain JS objects — they're stringified for you.
 *
 * Usage:
 *   <JsonLd data={breadcrumbSchema(...)} />
 *
 * Schema reference: https://schema.org
 */

interface JsonLdProps {
  data: Record<string, unknown> | Record<string, unknown>[];
}

export const JsonLd = ({ data }: JsonLdProps) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

// ─── Helpers ────────────────────────────────────────────────────────────────

const BASE = "https://brickplease.com";

export const breadcrumbSchema = (
  items: Array<{ name: string; path: string }>,
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.path.startsWith("http") ? it.path : `${BASE}${it.path}`,
  })),
});

export const serviceSchema = (s: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  areaServed?: string | string[];
  duration?: string;
  audience?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: s.name,
  description: s.description,
  url: s.url.startsWith("http") ? s.url : `${BASE}${s.url}`,
  serviceType: s.serviceType ?? "AI Employee Delivery",
  provider: {
    "@type": "Organization",
    name: "Brickplease AI Enablement",
    url: BASE,
  },
  areaServed: s.areaServed ?? ["Sweden", "European Union", "Worldwide"],
  ...(s.duration && { termsOfService: s.duration }),
  ...(s.audience && {
    audience: { "@type": "BusinessAudience", audienceType: s.audience },
  }),
});

export const faqSchema = (qa: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map((it) => ({
    "@type": "Question",
    name: it.question,
    acceptedAnswer: { "@type": "Answer", text: it.answer },
  })),
});

export const definedTermSetSchema = (set: {
  name: string;
  url: string;
  description: string;
  terms: Array<{ term: string; definition: string }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  name: set.name,
  description: set.description,
  url: set.url.startsWith("http") ? set.url : `${BASE}${set.url}`,
  hasDefinedTerm: set.terms.map((t) => ({
    "@type": "DefinedTerm",
    name: t.term,
    description: t.definition,
    inDefinedTermSet: set.url.startsWith("http") ? set.url : `${BASE}${set.url}`,
  })),
});

export const personSchema = (p: {
  name: string;
  jobTitle: string;
  url?: string;
  image?: string;
  sameAs?: string[];
  worksFor?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: p.name,
  jobTitle: p.jobTitle,
  ...(p.url && { url: p.url }),
  ...(p.image && { image: p.image }),
  ...(p.sameAs && { sameAs: p.sameAs }),
  worksFor: {
    "@type": "Organization",
    name: p.worksFor ?? "Brickplease AI Enablement",
    url: BASE,
  },
});

